/**
 * 图片上传组件
 * 处理用户图片上传和预设图片选择
 */
import { i18n } from '../core/I18nManager';

export interface UploaderConfig {
    maxFileSize: number;       // 最大文件大小（字节）
    acceptedTypes: string[];   // 接受的文件类型
    presetImages: PresetImage[];
}

export interface PresetImage {
    id: string;
    name: string;
    url: string;
    thumbnail?: string;
}

export interface UploaderEvents {
    onImageSelected?: (source: string | File, isPreset: boolean) => void;
    onError?: (message: string) => void;
}

export class ImageUploader {
    private container: HTMLElement;
    private config: UploaderConfig;
    private events: UploaderEvents = {};
    private fileInput: HTMLInputElement | null = null;

    constructor(container: HTMLElement, config?: Partial<UploaderConfig>) {
        this.container = container;
        this.config = {
            maxFileSize: 10 * 1024 * 1024,  // 10MB
            acceptedTypes: ['image/jpeg', 'image/png', 'image/webp'],
            presetImages: [],
            ...config
        };

        this.render();
    }

    /**
     * 设置事件监听
     */
    setEvents(events: UploaderEvents): void {
        this.events = { ...this.events, ...events };
    }

    /**
     * 设置预设图片
     */
    setPresetImages(images: PresetImage[]): void {
        this.config.presetImages = images;
        this.render();
    }

    /**
     * 渲染 UI
     */
    private render(): void {
        this.container.innerHTML = `
            <div class="image-uploader">
                <div class="uploader-section">
                    <h4>${i18n.t('upload_custom_title')}</h4>
                    <div class="upload-area" id="upload-drop-zone">
                        <div class="upload-icon">📁</div>
                        <p>${i18n.t('upload_area_msg')}</p>
                        <p class="upload-hint">${i18n.t('upload_hint')}</p>
                        <input type="file" id="image-file-input" accept="image/*" hidden />
                    </div>
                </div>

                ${this.config.presetImages.length > 0 ? `
                    <div class="uploader-section">
                        <h4>${i18n.t('select_preset_title')}</h4>
                        <div class="preset-gallery" id="preset-gallery">
                            ${this.config.presetImages.map(img => `
                                <div class="preset-item" data-id="${img.id}" data-url="${img.url}">
                                    <img src="${img.thumbnail || img.url}" alt="${i18n.t(img.name)}" />
                                    <span>${i18n.t(img.name)}</span>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}
            </div>
        `;

        this.bindEvents();
    }

    /**
     * 绑定事件
     */
    private bindEvents(): void {
        // 文件输入
        this.fileInput = this.container.querySelector('#image-file-input');
        if (this.fileInput) {
            this.fileInput.addEventListener('change', this.handleFileSelect.bind(this));
        }

        // 拖放区域
        const dropZone = this.container.querySelector('#upload-drop-zone') as HTMLElement;
        if (dropZone) {
            dropZone.addEventListener('click', () => this.fileInput?.click());
            dropZone.addEventListener('dragover', (e) => this.handleDragOver(e as DragEvent));
            dropZone.addEventListener('dragleave', (e) => this.handleDragLeave(e as DragEvent));
            dropZone.addEventListener('drop', (e) => this.handleDrop(e as DragEvent));
        }

        // 预设图片
        const presetItems = this.container.querySelectorAll('.preset-item');
        presetItems.forEach(item => {
            item.addEventListener('click', () => {
                const url = item.getAttribute('data-url');
                if (url) {
                    this.selectPresetImage(url);
                }
            });
        });
    }

    /**
     * 处理文件选择
     */
    private handleFileSelect(e: Event): void {
        const input = e.target as HTMLInputElement;
        const file = input.files?.[0];

        if (file) {
            this.processFile(file);
        }
    }

    /**
     * 处理拖拽悬停
     */
    private handleDragOver(e: DragEvent): void {
        e.preventDefault();
        e.stopPropagation();

        const dropZone = e.currentTarget as HTMLElement;
        dropZone.classList.add('drag-over');
    }

    /**
     * 处理拖拽离开
     */
    private handleDragLeave(e: DragEvent): void {
        e.preventDefault();
        e.stopPropagation();

        const dropZone = e.currentTarget as HTMLElement;
        dropZone.classList.remove('drag-over');
    }

    /**
     * 处理文件拖放
     */
    private handleDrop(e: DragEvent): void {
        e.preventDefault();
        e.stopPropagation();

        const dropZone = e.currentTarget as HTMLElement;
        dropZone.classList.remove('drag-over');

        const file = e.dataTransfer?.files[0];
        if (file) {
            this.processFile(file);
        }
    }

    /**
     * 处理文件
     */
    private processFile(file: File): void {
        // 验证文件类型
        if (!this.config.acceptedTypes.includes(file.type)) {
            this.events.onError?.(i18n.t('msg_invalid_file_format'));
            return;
        }

        // 验证文件大小
        if (file.size > this.config.maxFileSize) {
            const maxMB = this.config.maxFileSize / (1024 * 1024);
            this.events.onError?.(i18n.t('msg_file_too_large', { max: maxMB }));
            return;
        }

        this.events.onImageSelected?.(file, false);
    }

    /**
     * 选择预设图片
     */
    private selectPresetImage(url: string): void {
        // 高亮选中项
        const items = this.container.querySelectorAll('.preset-item');
        items.forEach(item => item.classList.remove('selected'));

        const selectedItem = this.container.querySelector(`[data-url="${url}"]`);
        selectedItem?.classList.add('selected');

        this.events.onImageSelected?.(url, true);
    }

    /**
     * 重置
     */
    reset(): void {
        if (this.fileInput) {
            this.fileInput.value = '';
        }

        const items = this.container.querySelectorAll('.preset-item');
        items.forEach(item => item.classList.remove('selected'));
    }

    /**
     * 显示/隐藏
     */
    show(): void {
        this.container.style.display = 'block';
    }

    hide(): void {
        this.container.style.display = 'none';
    }

    /**
     * 销毁
     */
    destroy(): void {
        this.container.innerHTML = '';
    }
}
